import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBuilding } from '@fortawesome/free-solid-svg-icons'
import { enterpriseAutocomplete, postEnterprise } from 'api/modules/enterprises'
import { REF_KEYS_LIST } from 'constants/onboarding'
import { FormattedMessage } from 'locales/index'
import React from 'react'
import { AppRemoteAutoComplete, AutoCompleteValueProps } from 'shared/components/common/AppRemoteSelect'
import { SelectProps, FormInstance, DefaultOptionType } from '@commutifi-fe/ui'
import { useAsyncFn } from 'shared/hooks/useAsyncFn'
import { FormItemInjectedProps } from 'shared/interfaces'
import { normalizeString } from 'utils/helpers'
import { generateNewEntityOptionKey, isNewEntity } from 'utils/helpers/autocompleteCreation'
import * as logRocket from 'utils/logRocket'

interface IEnterpriseSuggestion {
  id: number
  name: string
  address?: string
  lat?: number
  lng?: number
}

export interface SuggestCompanySelectProps
  extends Omit<SelectProps, 'onChange' | 'value'>,
    FormItemInjectedProps<AutoCompleteValueProps | string | undefined> {
  // OrganizationId to narrow down to the enterprises associated with this organization
  organizationId: string
  // Allow to create a custom entity with the user
  // input (not in the Select dropdown list)
  allowCreation?: boolean
  // Placeholder text
  placeholder?: string
  disabled?: boolean
  // Callback function when the user select a new value
  onSelect?: (value: AutoCompleteValueProps | string) => void
  form: FormInstance
}

const suggestEnterprises = (organizationId: string) => async (searchedTerm: string) => {
  const { suggestions }: { suggestions: IEnterpriseSuggestion[] } = await enterpriseAutocomplete({
    ...(organizationId && { organizationId }),
    q: searchedTerm
  })
  return suggestions || []
}

const createEnterprise = (organizationId: string) => (name: string) =>
  postEnterprise({
    ...(organizationId && { organizationId }),
    name
  })

export const CREATION_ERROR = 'CREATION_FAILED'
export const validateCompany = (rule: any, enterprise: AutoCompleteValueProps | string) => {
  if (enterprise === CREATION_ERROR) {
    // eslint-disable-next-line prefer-promise-reject-errors -- Legacy, can be fixed
    return Promise.reject(<FormattedMessage id="onboarding.companySelector.error.creation" />)
  }

  // We must have an enterprise id defined
  if (!enterprise || (typeof enterprise === 'object' && !enterprise.key)) {
    // eslint-disable-next-line prefer-promise-reject-errors -- Legacy, can be fixed
    return Promise.reject(<FormattedMessage id="onboarding.selector.error.required" />)
  }

  return Promise.resolve()
}

// Here we don't forward ref because we create the ref to be able to control
// if the autocomplete is focus, blurred, etc. in the component 'AppAutoComplete'
function SuggestCompanySelect({
  organizationId,
  allowCreation,
  placeholder,
  id,
  value,
  disabled,
  onChange,
  onSelect,
  form,
  ...rest
}: SuggestCompanySelectProps) {
  const searchEnterprises = suggestEnterprises(organizationId)
  const createEnterpriseRequest = createEnterprise(organizationId)
  const [{ loading: isCreating }, create] = useAsyncFn(createEnterpriseRequest, [], {
    shouldThrow: true
  })

  const handleAnalytics = (enterpriseId: string | number | null) => {
    logRocket.identify({ surveyEnterpriseId: enterpriseId || '', surveyOfficeId: '' })
  }

  const triggerSelect = (value: AutoCompleteValueProps | string) => {
    if (onChange) {
      onChange(value)
    }
    if (onSelect) {
      onSelect(value)
    }
  }

  const handleCreateEnterprise = async (name: string) => {
    try {
      const enterprise = await create(name)
      handleAnalytics(enterprise.id)
      triggerSelect({ key: enterprise.id, label: name })
    } catch (e) {
      onSelect && onSelect(CREATION_ERROR)
    }
  }

  const handleSelect = (value: string, option: any) => {
    // If the user selected an option where we need to create a new enterprise
    // we want to avoid selecting this option because we will go through the
    // process of creating that enterprise and call onChange afterward
    if (option && typeof option.key === 'string' && isNewEntity(option.key)) {
      return handleCreateEnterprise(value)
    }

    handleAnalytics(option.key)

    const newValue = { key: typeof option.key === 'string' ? option.key : value, label: value }
    return triggerSelect(newValue)
  }

  const handleOnChange = (inputValue: string) => {
    // This is for when the office selector is in the same section than the company selector.
    // We reset the office selected value to be sure the user selects an office from that new company.
    // Also, the redux state will be updated to make sure the value set in the survey payload is
    // reset as well
    if (form.getFieldValue(REF_KEYS_LIST.accountOfficeId)) {
      form.setFieldsValue({ [REF_KEYS_LIST.accountOfficeId]: null })
    }
    onChange && onChange(!inputValue || inputValue === '' ? undefined : { label: inputValue })
  }

  const renderOptions = (options: IEnterpriseSuggestion[], searchTerm: string) => {
    // Propose to add a company with the search term if it is not already
    // in the options
    const addSearchTerm =
      searchTerm &&
      searchTerm !== '' &&
      allowCreation &&
      !options.some((enterprise) => normalizeString(enterprise.name) === normalizeString(searchTerm))

    const optionsChildren: DefaultOptionType[] = [
      ...options.map((enterprise) => ({
        key: `${enterprise.id}`,
        // We need to define the value here because we set optionLabelProp="value" so the value will be
        // the label for the selected item
        value: enterprise.name,
        label: enterprise.name
      }))
    ]

    if (addSearchTerm) {
      optionsChildren.push({
        key: 'create__group',
        label: (
          <div>
            <div className="u-divider" />
            <span>
              <FontAwesomeIcon className="u-margin-right--small" icon={faBuilding} />
              <FormattedMessage id="onboarding.companySelector.addNew.title" />
            </span>
          </div>
        ),
        options: [
          {
            key: generateNewEntityOptionKey(searchTerm),
            value: searchTerm,
            label: (
              <span>
                <FormattedMessage id="global.add" /> &quot;{searchTerm.trim()}&quot;
              </span>
            )
          }
        ]
      })
    }
    return optionsChildren
  }

  return (
    <AppRemoteAutoComplete<IEnterpriseSuggestion, string>
      id={id}
      request={searchEnterprises}
      value={typeof value !== 'string' ? value?.label : value}
      placeholder={placeholder}
      onSelect={handleSelect}
      onSearch={handleOnChange}
      renderOptions={renderOptions}
      notFound={<FormattedMessage id="components.select.company.notFound" />}
      loading={isCreating}
      disabled={disabled || isCreating}
      optionLabelProp="value"
      {...rest}
    />
  )
}

export default SuggestCompanySelect
